import React, { useMemo, useRef, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioButton,
} from '../../components/BrioComponents';
import { parseItemsFromXlsx, buildXlsxLookups } from '../utils/projectItemsXlsx';

const STAGE = {
  PICK: 'pick',
  PARSING: 'parsing',
  REVIEW: 'review',
  IMPORTING: 'importing',
};

function ImportItemsModal({ versions, zonesById, onClose, onImport }) {
  const [stage, setStage] = useState(STAGE.PICK);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const fileInputRef = useRef(null);

  const lookups = useMemo(
    () => buildXlsxLookups({ versions, zonesById }),
    [versions, zonesById]
  );

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setStage(STAGE.PARSING);
    setSubmitError(null);
    try {
      const result = await parseItemsFromXlsx(file, lookups);
      setRows(result.rows);
      setErrors(result.errors);
      setStage(STAGE.REVIEW);
    } catch (err) {
      console.error('[ImportItemsModal] parse failed', err);
      setSubmitError(`Couldn't read the file: ${err.message}`);
      setStage(STAGE.PICK);
    }
  };

  const validRows = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const invalidCount = rows.length - validRows.length;

  const handleConfirm = async () => {
    if (validRows.length === 0) return;
    setStage(STAGE.IMPORTING);
    setSubmitError(null);
    const inputs = validRows.map((r) => ({
      itemVersionId: r.itemVersionId,
      quantity: r.quantity,
      rateQuoted: r.rateQuoted,
      rateBilled: r.rateBilled,
      projectZoneId: r.projectZoneId,
      descriptionOverride: r.descriptionOverride,
      sequenceOrder: r.sequenceOrder,
    }));
    const result = await onImport(inputs);
    if (result.success) {
      onClose();
    } else {
      setSubmitError(result.error || 'Import failed.');
      setStage(STAGE.REVIEW);
    }
  };

  let footer;
  if (stage === STAGE.PICK || stage === STAGE.PARSING) {
    footer = <BrioButton variant="secondary" onClick={onClose}>Cancel</BrioButton>;
  } else if (stage === STAGE.REVIEW || stage === STAGE.IMPORTING) {
    footer = (
      <>
        <BrioButton variant="secondary" onClick={onClose} disabled={stage === STAGE.IMPORTING}>Cancel</BrioButton>
        <BrioButton
          variant="primary"
          onClick={handleConfirm}
          disabled={validRows.length === 0 || stage === STAGE.IMPORTING}
        >
          {stage === STAGE.IMPORTING
            ? 'Importing…'
            : invalidCount > 0
              ? `Import ${validRows.length} valid (skip ${invalidCount})`
              : `Import ${validRows.length} line${validRows.length === 1 ? '' : 's'}`}
        </BrioButton>
      </>
    );
  }

  return (
    <BrioModal title="Import BOQ from Excel" onClose={onClose} width="xl" footer={footer}>
      <BrioModalBody>
        {submitError && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {submitError}
          </div>
        )}

        {stage === STAGE.PICK && (
          <div>
            <p style={{ marginTop: 0 }}>
              Choose an <code>.xlsx</code> file with columns <strong>Code</strong>, <strong>Description</strong>, <strong>Unit</strong>, <strong>Quantity</strong>, <strong>Zone</strong>, <strong>Order</strong>.
              The simplest path is to export an existing project first, edit it in Excel, and import it back here.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
            <BrioButton variant="primary" onClick={() => fileInputRef.current?.click()}>
              Choose Excel file…
            </BrioButton>
            <div style={{ marginTop: '1rem', fontSize: '0.82rem', color: 'var(--brio-text-muted)' }}>
              Rows with an unknown library code or unknown zone will be flagged and skipped — they don't block valid rows.
            </div>
          </div>
        )}

        {stage === STAGE.PARSING && (
          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--brio-text-muted)' }}>
            Reading {fileName}…
          </div>
        )}

        {(stage === STAGE.REVIEW || stage === STAGE.IMPORTING) && (
          <div>
            <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
              <strong>{rows.length}</strong> row{rows.length === 1 ? '' : 's'} read from <code>{fileName}</code>:
              &nbsp;<span style={{ color: '#2e7d32' }}>{validRows.length} valid</span>
              {invalidCount > 0 && <span>, <span style={{ color: '#a94442' }}>{invalidCount} with errors</span></span>}.
            </div>
            <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '0.4rem 0.5rem', width: 40 }} />
                    <th style={{ padding: '0.4rem 0.5rem' }}>Row</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Code</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Description</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Zone</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const ok = r.errors.length === 0;
                    return (
                      <tr key={r.rowNumber} style={{
                        borderTop: '1px solid var(--brio-border-subtle, #eee)',
                        background: ok ? 'transparent' : '#fdecec',
                      }}>
                        <td style={{ padding: '0.35rem 0.5rem' }}>
                          <span title={ok ? 'Ready to import' : 'Will be skipped'} style={{ color: ok ? '#2e7d32' : '#a94442' }}>
                            {ok ? '✓' : '✗'}
                          </span>
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', color: 'var(--brio-text-muted)' }}>{r.rowNumber}</td>
                        <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace' }}>{r.code}</td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>
                          {r.descriptionOverride || r.libraryDescription || '—'}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                          {r.quantity ?? <span style={{ color: '#a94442' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{r.zonePath || <span style={{ color: 'var(--brio-text-muted)' }}>—</span>}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color: '#a94442', fontSize: '0.78rem' }}>
                          {r.errors.join(' · ')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {errors.length > 0 && validRows.length === 0 && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#a94442' }}>
                Nothing to import. Fix the issues above (often: missing library items) and retry.
              </div>
            )}
          </div>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default ImportItemsModal;
